import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, ArrowRight, ShieldCheck } from "lucide-react";
import axios from "axios";
import { INV_PORTAL_API } from "@/lib/investorPortalApi";
import { useInvestorPortalAuth } from "@/contexts/InvestorPortalAuthContext";

const _inFlight = new Map();

export default function InvestorVerify() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeLogin } = useInvestorPortalAuth();
  const token = params.get("token");

  // "checking" | "2fa" | "error" | "done"
  const [state, setState] = useState({ status: "checking", message: "" });
  const [twofa, setTwofa] = useState({ tempToken: "", code: "", loading: false, error: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ status: "error", message: t("invPortal.verify.missingToken") });
        return;
      }
      try {
        let promise = _inFlight.get(token);
        if (!promise) {
          promise = axios.post(
            `${INV_PORTAL_API}/magic-link/verify`,
            { token },
            { withCredentials: true },
          );
          _inFlight.set(token, promise);
        }
        const { data } = await promise;
        if (cancelled) return;

        if (data.requires_2fa) {
          setTwofa((p) => ({ ...p, tempToken: data.temp_session_token }));
          setState({ status: "2fa", message: "" });
          return;
        }

        await completeLogin(data);
        navigate("/portal/investor/dashboard", { replace: true });
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "error",
          message: err?.response?.status === 401
            ? t("invPortal.verify.linkInvalid")
            : t("invPortal.verify.errorGeneric"),
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token, completeLogin, navigate, t]);

  const submit2fa = async (e) => {
    e.preventDefault();
    if (twofa.loading || !twofa.code.trim()) return;
    setTwofa((p) => ({ ...p, loading: true, error: "" }));
    try {
      const { data } = await axios.post(
        `${INV_PORTAL_API}/2fa/challenge`,
        { temp_session_token: twofa.tempToken, code: twofa.code.trim() },
        { withCredentials: true },
      );
      await completeLogin(data);
      navigate("/portal/investor/dashboard", { replace: true });
    } catch (err) {
      setTwofa((p) => ({
        ...p,
        loading: false,
        error: err?.response?.status === 401
          ? t("invPortal.verify.codeInvalid")
          : t("invPortal.verify.errorGeneric"),
      }));
    }
  };

  return (
    <div className="min-h-screen bg-porcelain flex items-center justify-center p-5" data-testid="investor-portal-verify">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-md bg-white border border-sandBeige rounded-[2rem] shadow-soft p-10 text-center"
      >
        {state.status === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-raspberry" size={28} />
            <p className="font-serif text-2xl text-cocoa mt-5" data-testid="inv-verify-loading">
              {t("invPortal.verify.loading")}
            </p>
            <p className="text-warmGrey text-sm mt-2">{t("invPortal.verify.loadingHint")}</p>
          </>
        )}

        {state.status === "2fa" && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-skyMist text-cocoa flex items-center justify-center">
              <ShieldCheck size={22} />
            </div>
            <p className="font-serif text-2xl text-cocoa mt-5" data-testid="inv-verify-2fa-title">
              {t("invPortal.verify.twofaTitle")}
            </p>
            <p className="text-warmGrey text-sm mt-3">{t("invPortal.verify.twofaHint")}</p>
            <form onSubmit={submit2fa} className="mt-6 flex flex-col gap-4" data-testid="inv-verify-2fa-form">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={12}
                value={twofa.code}
                onChange={(e) => setTwofa((p) => ({ ...p, code: e.target.value }))}
                placeholder="123456"
                className="w-full text-center text-2xl tracking-[0.3em] bg-porcelain border border-sandBeige rounded-2xl px-4 py-4 focus:border-raspberry focus:outline-none transition-colors"
                data-testid="inv-verify-2fa-input"
              />
              {twofa.error && (
                <p className="text-coral text-sm" data-testid="inv-verify-2fa-error">{twofa.error}</p>
              )}
              <button
                type="submit"
                disabled={twofa.loading}
                className="bg-raspberry hover:bg-coral disabled:opacity-60 text-white rounded-full px-7 py-3.5 font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="inv-verify-2fa-submit"
              >
                {twofa.loading ? <Loader2 size={16} className="animate-spin" /> : t("invPortal.verify.twofaSubmit")}
              </button>
              <p className="text-xs text-warmGrey">{t("invPortal.verify.twofaRecoveryHint")}</p>
            </form>
          </>
        )}

        {state.status === "error" && (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-roseMilk text-coral flex items-center justify-center">
              <AlertCircle size={22} />
            </div>
            <p className="font-serif text-2xl text-cocoa mt-5" data-testid="inv-verify-error-title">
              {t("invPortal.verify.errorTitle")}
            </p>
            <p className="text-warmGrey text-sm mt-3">{state.message}</p>
            <Link
              to="/portal/investor/login"
              className="mt-7 inline-flex items-center gap-2 bg-raspberry hover:bg-coral text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
              data-testid="inv-verify-retry"
            >
              {t("invPortal.verify.retry")} <ArrowRight size={14} />
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
